import { memo, useMemo, useState, useCallback } from 'react';

import { List } from 'components/list';
import { formatDate } from 'utils/date';
import { capitalize } from 'utils/string';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { isMobile, useDownloadFile } from 'hooks';
import { ListHeader } from 'components/list-header';
import { CategoryDetails } from 'store/api/event/types';
import { Drawer, DrawerHeader } from 'components/drawer';
import { DownloadButton } from 'components/specified-buttons';

import { ExpenseDetails } from './expense-details';
import { CategoryDetailsDialogProps } from './types';
import { ReimbursementDetails } from './reimbursement-details';

const template = '88px repeat(2, 64px) 1fr 1fr 1fr 24px';
const titles = [
  'Date',
  'Amount',
  'Method',
  'Notes',
  'Employees',
  'Created By',
  '',
];
const cellsMobile = [
  { row: '2/3', col: '1/2' },
  { row: '2/3', col: '2/3' },
  { row: '3/4', col: '1/3' },
  { row: '1/2', col: '1/3' },
  { row: '4/5', col: '1/3' },
  { row: '5/6', col: '1/2' },
  { row: '5/6', col: '2/3' },
];

const DownloadCell = memo(
  ({ url, onDownload }: { url: string; onDownload: (url: string) => void }) => (
    <div>
      <DownloadButton url={url} onDownloadFile={onDownload} />
    </div>
  )
);

const CategoryExpenseCard = memo(
  ({
    value,
    onSelect,
    downloadFile,
  }: {
    value: CategoryDetails;
    downloadFile: (url: string) => void;
    onSelect: (id: number, source: string) => void;
  }) => {
    const handleClick = useCallback(
      () => onSelect(value.id, value.source),
      [value.id, value.source, onSelect]
    );

    const values = useMemo(
      () => [
        formatDate(value.date),
        formatPrice(value.amount),
        value?.paymentMethod && capitalize(value?.paymentMethod),
        value?.notes,
        value?.employeeName,
        value?.createdBy?.name,
        value?.receiptUrl && (
          <DownloadCell
            key="download"
            url={value.receiptUrl}
            onDownload={downloadFile}
          />
        ),
      ],
      [value, downloadFile]
    );

    return (
      <ListCard
        key={value.id}
        values={values}
        headers={titles}
        template={template}
        onClick={handleClick}
        cellsMobile={cellsMobile}
      />
    );
  }
);

export const CategoryDetailsDrawer = memo(
  ({
    open,
    onClose,
    categoryName,
    categoryDetails,
  }: CategoryDetailsDialogProps) => {
    const mobile = isMobile();
    const { downloadFile } = useDownloadFile();
    const [selectedExpenseId, setSelectedExpenseId] = useState<number>();
    const [selectedReimbursementId, setSelectedReimbursementId] =
      useState<number>();

    const categoryExpenses = useMemo(
      () =>
        categoryDetails?.filter(
          (value) => value.categoryName === categoryName
        ) || [],
      [categoryName, categoryDetails]
    );

    const handleSelect = (id: number, source: string) => {
      if (source === 'expense') {
        setSelectedExpenseId(id);
      } else {
        setSelectedReimbursementId(id);
      }
    };

    const expensesList = useMemo(
      () =>
        categoryExpenses?.map((value) => (
          <CategoryExpenseCard
            value={value}
            key={value.id}
            onSelect={handleSelect}
            downloadFile={downloadFile}
          />
        )),
      [categoryExpenses, handleSelect, downloadFile]
    );

    if (!open) {
      return null;
    }

    return (
      <Drawer hideOverlay opened={true} topOffset={mobile ? '10%' : '15%'}>
        <DrawerHeader onClose={onClose} title="Category Details" />

        {!selectedReimbursementId && !selectedExpenseId && (
          <List
            count={categoryExpenses?.length}
            mainWrapperStyles={{ padding: 0 }}
            header={
              <ListHeader padding="8px" titles={titles} template={template} />
            }
          >
            {expensesList}
          </List>
        )}

        {selectedReimbursementId && (
          <ReimbursementDetails
            selectedReimbursementId={selectedReimbursementId}
            onClose={() => setSelectedReimbursementId(undefined)}
          />
        )}

        {selectedExpenseId && (
          <ExpenseDetails
            selectedExpenseId={selectedExpenseId}
            onClose={() => setSelectedExpenseId(undefined)}
          />
        )}
      </Drawer>
    );
  }
);
